/**
 * Add Subdomain Modal Component
 * Allows manual addition of subdomains to existing domain packs
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Plus,
  AlertCircle,
  Loader2,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';

import { addSubdomain } from '../../lib/api/domainPacksApi';

interface AddSubdomainModalProps {
  domainPackId: string;
  domainPackName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  subdomainName: string;
  description: string;
  scope: string;
  runResearch: boolean;
}

export const AddSubdomainModal: React.FC<AddSubdomainModalProps> = ({
  domainPackId,
  domainPackName,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    subdomainName: '',
    description: '',
    scope: '',
    runResearch: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Mutation for adding subdomain
  const addMutation = useMutation({
    mutationFn: () =>
      addSubdomain(
        domainPackId,
        formData.subdomainName,
        formData.description || undefined,
        formData.scope || undefined,
        formData.runResearch
      ),
    onSuccess: (data) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['domain-pack', domainPackId] });
      queryClient.invalidateQueries({ queryKey: ['domain-packs'] });

      // Navigate to progress page if research started
      if (data.job_id) {
        navigate(
          `/domain-packs/${domainPackId}/progress?job_id=${data.job_id}&subdomain_id=${data.subdomain_id}`
        );
      } else {
        // Just close modal if no research
        handleClose();
      }
    },
    onError: (error: any) => {
      const detail = error.response?.data?.detail || 'Failed to add subdomain';
      setErrors({ submit: detail });
    },
  });

  const handleClose = () => {
    setFormData({
      subdomainName: '',
      description: '',
      scope: '',
      runResearch: true,
    });
    setErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.subdomainName.trim()) {
      newErrors.subdomainName = 'Subdomain name is required';
    } else if (formData.subdomainName.length < 3) {
      newErrors.subdomainName = 'Subdomain name must be at least 3 characters';
    } else if (formData.subdomainName.length > 200) {
      newErrors.subdomainName = 'Subdomain name must be less than 200 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (formData.scope && formData.scope.length > 500) {
      newErrors.scope = 'Scope must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    addMutation.mutate();
  };

  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">
              Add New Subdomain
            </h2>
            <p className="text-sm text-neutral-600 mt-1">
              Add to: <span className="font-semibold">{domainPackName}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            disabled={addMutation.isPending}
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Subdomain Name */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">
              Subdomain Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.subdomainName}
              onChange={(e) =>
                handleInputChange('subdomainName', e.target.value)
              }
              placeholder="e.g., Demand Forecasting, Inventory Optimization"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.subdomainName ? 'border-red-500' : 'border-neutral-300'
              }`}
              disabled={addMutation.isPending}
              autoFocus
            />
            {errors.subdomainName && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.subdomainName}
              </p>
            )}
            <p className="mt-2 text-sm text-neutral-600">
              A specific area within {domainPackName} to research
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">
              Description <span className="text-neutral-500">(optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description of what this subdomain covers..."
              rows={3}
              maxLength={500}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${
                errors.description ? 'border-red-500' : 'border-neutral-300'
              }`}
              disabled={addMutation.isPending}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                Help the AI understand the focus of this subdomain
              </p>
              <span className="text-xs text-neutral-500">
                {formData.description.length}/500
              </span>
            </div>
            {errors.description && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
          </div>

          {/* Scope */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">
              Scope <span className="text-neutral-500">(optional)</span>
            </label>
            <textarea
              value={formData.scope}
              onChange={(e) => handleInputChange('scope', e.target.value)}
              placeholder="Define the boundaries and coverage of this subdomain..."
              rows={3}
              maxLength={500}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${
                errors.scope ? 'border-red-500' : 'border-neutral-300'
              }`}
              disabled={addMutation.isPending}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                What's included and what's excluded?
              </p>
              <span className="text-xs text-neutral-500">
                {formData.scope.length}/500
              </span>
            </div>
            {errors.scope && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.scope}
              </p>
            )}
          </div>

          {/* Run Research Checkbox */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.runResearch}
                onChange={(e) =>
                  handleInputChange('runResearch', e.target.checked)
                }
                className="mt-1 w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                disabled={addMutation.isPending}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary-600" />
                  <span className="font-semibold text-neutral-900">
                    Run AI Research
                  </span>
                </div>
                <p className="text-sm text-neutral-700 mt-1">
                  Automatically research trends, issues, best practices, data
                  elements, and hypotheses for this subdomain (10-15 minutes)
                </p>
              </div>
            </label>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Failed to add subdomain</p>
                <p className="text-sm text-red-700 mt-1">{errors.submit}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              disabled={addMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={addMutation.isPending}
            >
              {addMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Subdomain
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
